import React from "react";
import styled from "styled-components";
import AniLink from "gatsby-plugin-transition-link/AniLink";

import {
  breakpoints,
  colors,
  fontFamily,
  getProjectTheme,
  sectionRhythm,
} from "../../../theme";
import { PROJECT_MELTWATER } from "../../utils/constants/projects";
import { projectImage } from "../../utils/cdn";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";

const projectTheme = getProjectTheme("meltwater");

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0;
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

const TextLink = styled(AniLink)`
  color: inherit;
  text-decoration: underline;
`;

const MeltwaterProjectSection = () => {
  const { client, services, industry, techStack, links, heading, media, roles, year } =
    PROJECT_MELTWATER;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <ContainerScroll
          titleComponent={<Title>{heading}</Title>}
          role={roles.join(", ")}
          year={yearLabel}
        >
          <ProjectHero
            src={media.cover.desktop}
            alt="Meltwater publishing calendar"
          />
        </ContainerScroll>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>The product</h4>
            <p>
              I was a lead frontend engineer on Meltwater's publishing product.
              Social teams used to draft, schedule, and publish across different
              accounts from one calendar section, and handled replies and
              comments without leaving the application.
            </p>
            <p>
              The stack comprised of Vue with Stencil web components on a
              micro-frontend. We used FullCalendar to handle most of the
              calendar section. I owned rendering, the flows around it, platform
              validations, in-flow media editing, and an AI assistant for
              helping with writing and tagging people.
            </p>
          </section>

          <section>
            <h4>Publishing workflow</h4>
            <p>
              Posts moved from draft to scheduled to published inside the
              calendar. Users filtered by account, region, or content type, then
              edited copy, media, or timing without leaving the view.
            </p>
            <p>
              Each platform had its own rules. I added upfront checks for
              character limits, media dimensions, aspect ratios, and URLs so
              errors showed before publish, specific enough to fix on the spot.
            </p>
            <p>
              Crop and trim lived in the same flow. I shipped an AI assistant
              for writing and tagging. Suggestions stayed optional and needed an
              explicit accept or reject before anything went live.
            </p>
            <ContentImage
              src={projectImage("meltwater-engage-schedule-modal")}
              alt="Publishing calendar with scheduled posts"
            />
          </section>

          <section>
            <h4>Calendar week view</h4>
            <p>
              Product wanted week view vertical. FullCalendar v3's web component
              build did not support that layout. Day and stacked views could
              stay on the library. The week view could not. A full migration
              costed around $200K and three months of dedicated work.
            </p>
            <p>
              I built a custom week-view renderer instead. Drag-and-drop, event
              handling, and view switching stayed intact, synced with
              FullCalendar's internal data structures. The full write-up is in
              the{" "}
              <TextLink cover bg={colors.black} to="/blog/meltwater-calendar/">
                week-view renderer case study
              </TextLink>
              .
            </p>
            <ContentImage
              src={projectImage("VerticalCalender")}
              alt="Vertical week view in the publishing calendar"
            />
          </section>

          <section>
            <h4>Shared component library</h4>
            <p>
              Several product teams had rebuilt the same UI in their own stacks.
              Buttons, dropdowns, modals, and inputs existed in multiple places
              with no shared source. The look drifted, and every fix got
              repeated.
            </p>
            <p>
              I worked on a shared Stencil web components library teams could
              load into Vue apps without a rewrite. We shipped 70+ documented
              components, kept them presentational with slots instead of props,
              and published through npm with Storybook and semantic versioning
              so fixes landed once.
            </p>
            <ContentImage
              src={projectImage("meltwater-component-decentralized-preview")}
              alt="Shared component library across product teams"
            />
          </section>

          <section>
            <h4>Performance under load</h4>
            <p>
              Some accounts started hanging. A single tab sat around 2.65GB of
              RAM. Support suspected the calendar could not handle large
              schedules. Heap snapshots and performance traces said otherwise.
            </p>
            <p>
              I traced detached DOM under Shadow DOM, long main-thread tasks,
              modals that mounted closed and leaked on reopen, and an
              unvirtualized tags list on the order of 10,000 items. Lazy-loaded
              modals plus cleanup brought memory to around 600MB. List
              virtualization settled it around 200MB. More detail is in the{" "}
              <TextLink
                cover
                bg={colors.black}
                to="/blog/meltwater-ram-optimization/"
              >
                memory optimization case study
              </TextLink>
              .
            </p>
            <ContentImage
              src={projectImage("RamConsumptionComparisonBlue")}
              alt="RAM usage before and after optimization"
            />
          </section>

          <section>
            <h4>Outcome</h4>
            <p>
              Validations, in-flow media editing, and the AI assistant shipped
              on top of an already busy calendar without a stack rewrite. Week
              view went vertical with a scoped renderer. Memory held near 200MB
              under continued use. The shared library cut duplicated UI work
              across teams.
            </p>
            <p>
              The approach stayed the same across all of it. Measure first, rule
              out the easy theory, and own the hard path only where the existing
              stack could not go.
            </p>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default MeltwaterProjectSection;
